import type { Item } from "../api";
import { formatManifestMetaParts } from "../utils/manifestMeta";
import {
    applyTallyQuantity,
    ensureTallyStructure,
    setTallyQuantity,
    wireEditableTallyCount,
} from "./tallyControl";

export type { TallySlideDirection } from "./tallyControl";
export { applyTallyQuantity as applyManifestRowQuantity };

export interface ManifestRowHandlers {
    onDecrement: (item: Item) => void;
    onIncrement: (item: Item) => void;
    onQuantitySet: (item: Item, quantity: number) => void;
}

export function renderManifestRow(item: Item, handlers: ManifestRowHandlers): HTMLElement {
    const outOfStock = item.quantity <= 0;
    const row = document.createElement("div");
    row.className = `manifest-row${outOfStock ? " manifest-row--out-of-stock" : ""}`;
    row.dataset.itemId = String(item.id);

    const info = document.createElement("div");
    info.className = "manifest-info";

    const name = document.createElement("span");
    name.className = "manifest-name";
    name.textContent = item.name;

    const meta = document.createElement("span");
    meta.className = "manifest-meta";
    const parts = formatManifestMetaParts(item);
    if (parts.prefix) {
        meta.append(document.createTextNode(`${parts.prefix} • `));
    }
    const expirySpan = document.createElement("span");
    expirySpan.className = parts.expiryWarn
        ? "manifest-meta-expiry manifest-meta-expiry--warn"
        : "manifest-meta-expiry";
    expirySpan.textContent = parts.expiryText;
    meta.appendChild(expirySpan);

    info.appendChild(name);
    info.appendChild(meta);

    const tally = document.createElement("div");
    tally.className = "manifest-tally";

    const minusBtn = document.createElement("button");
    minusBtn.type = "button";
    minusBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        handlers.onDecrement(item);
    });

    const count = document.createElement("span");
    count.className = `tally-count${outOfStock ? " tally-count--empty" : ""}`;
    count.dataset.value = String(item.quantity);
    ensureTallyStructure(count, item.quantity);

    wireEditableTallyCount(count, {
        min: 0,
        getQuantity: () => Number(count.dataset.value ?? "0"),
        onCommit: (quantity) => handlers.onQuantitySet(item, quantity),
    });

    const plusBtn = document.createElement("button");
    plusBtn.type = "button";
    plusBtn.className = "tally-btn plus";
    plusBtn.setAttribute("aria-label", `Increase ${item.name} quantity`);
    plusBtn.textContent = "+";
    plusBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        handlers.onIncrement(item);
    });

    tally.appendChild(minusBtn);
    tally.appendChild(count);
    tally.appendChild(plusBtn);

    setTallyQuantity(row, item.name, item.quantity, { minusBtn });

    row.appendChild(info);
    row.appendChild(tally);

    return row;
}
